import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {Button} from '@/components/ui';
import Icon from '@/components/icons/Icon';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import usePaginatedData from '@/utils/usePaginatedData';
import UserSettingsModal from './UserSettingsModal';
import UserActivityModal from './UserActivityModal';
import UserLoginsModal from './UserLoginsModal';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import UserApiLoginsModal from './UserApiLoginsModal';
import UserAuditModal from './UserAuditModal';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import UserAssignAgentModal from './UserAssignAgentModal';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import CompactList from '@/components/ui/CompactList';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface User {
    id: number;
    username: string;
    email: string;
    lastLogin: string | null;
    nrOfLogins30Days: number;
    groups: Array<{value: string; label: string}>;
    agentName: string | null;
    isSuperuser: boolean;
    active: boolean;
}



interface PaginatedData<T> {
    data: T[];
    loading: boolean;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    pages: number[] | null;
}

const setSearchQueryState = async (
    setSearchQuery: (query: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const UserListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [activityModalOpen, setActivityModalOpen] = useState<boolean>(false);
    const [loginsModalOpen, setLoginsModalOpen] = useState<boolean>(false);
    const [apiLoginsModalOpen, setApiLoginsModalOpen] = useState<boolean>(false);
    const [auditModalOpen, setAuditModalOpen] = useState<boolean>(false);
    const [auditSubjectLabel, setAuditSubjectLabel] = useState<string>('');
    const [assignAgentModalOpen, setAssignAgentModalOpen] = useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = useState<number>(0);
    const [selectedUserName, setSelectedUserName] = useState<string>('');

    const [searchQuery, setSearchQuery] = useState<string>('');

    // permissions need to be updated to use the new permissions
    const userCanAccessUsersResource = hasPermission('fs.user.list', userData?.permissions, true);
    const canCreateUser = hasPermission('fs.user.create', userData?.permissions, true);
    const canChangeUser = hasPermission('fs.user.update', userData?.permissions, true);
    const canViewUserActivity = hasPermission('fs.user.activity_logs.read', userData?.permissions, true);
    const canViewUserLogins = hasPermission('fs.user.login_logs.read', userData?.permissions, true);
    const canViewUserApiLogins = hasPermission('fs.user.api_logins.read', userData?.permissions, true);
    const canViewUserAudit = hasPermission('fs.user_audit.analytics.read', userData?.permissions, true);
    const canAssignAgent = hasPermission('fs.agent.user_assignment.create', userData?.permissions, true);


    const {
        data: users,
        loading: loadingUsers,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.usersResource, {
        storeResolver: 'users',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });


    if (!userCanAccessUsersResource && !loadingUserData) {
        return (
            <>
                <ConditionalNav>

                </ConditionalNav>

                <HelpTextBlock helpText={i18n.t('ht_the_users.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const usersCleaned = users.filter((item: User) => item.username !== 'AnonymousUser');

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openLoginsModal = (userId: number): void => {
        setSelectedUserId(userId);
        setLoginsModalOpen(true);
    };

    const closeLoginsModal = (): void => {
        setLoginsModalOpen(false);
        setSelectedUserId(0);
    };

    const openApiLoginsModal = (userId: number): void => {
        setSelectedUserId(userId);
        setApiLoginsModalOpen(true);
    };

    const closeApiLoginsModal = (): void => {
        setApiLoginsModalOpen(false);
        setSelectedUserId(0);
    };

    const openAuditModal = (userId: number, userLabel: string): void => {
        setSelectedUserId(userId);
        setAuditSubjectLabel(userLabel);
        setAuditModalOpen(true);
    };

    const closeAuditModal = (): void => {
        setAuditModalOpen(false);
        setSelectedUserId(0);
        setAuditSubjectLabel('');
    };

    const openActivityModal = (userId: number): void => {
        setSelectedUserId(userId);
        setActivityModalOpen(true);
    };

    const closeActivityModal = (): void => {
        setActivityModalOpen(false);
        setSelectedUserId(0);
    };

    const openSettingsModal = (userId: number): void => {
        setSelectedUserId(userId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedUserId(0);
    };

    const openAssignAgentModal = (userId: number, userName: string): void => {
        setSelectedUserId(userId);
        setSelectedUserName(userName);
        setAssignAgentModalOpen(true);
    };

    const closeAssignAgentModal = (): void => {
        setAssignAgentModalOpen(false);
        setSelectedUserId(0);
    };


    const columns = [
        {
            id: 'usernameEmail',
            header: i18n.t('th_username_email.text'),
            accessorFn: (row: User) => `${row.username} (${row.email})`,
        },
        {
            id: 'lastLogin',
            header: i18n.t('th_last_login.text'),
            accessorFn: (row: User) => {
                if (!row.lastLogin || row.lastLogin === '-') {
                    return '-';
                }

                return configLoaded ? formatDateTime(row.lastLogin, true, {setZone: false},
                ) : '';
            },
        },
        {
            id: 'nrOfLogins30Days',
            header: i18n.t('th_days.text'),
            accessorKey: 'nrOfLogins30Days',
        },
        {
            id: 'groups',
            header: i18n.t('th_groups.text'),
            accessorKey: 'groups',
            cell: (cell: {getValue: () => Array<{value: string; label: string}>; row: {original: User}}) => {
                const groups = cell.getValue();
                const groupLabels = groups.map(group => group.label);
                const username = cell.row.original.username;
                return (
                    <CompactList
                        title={`${i18n.t('th_groups.text')} - ${username}`}
                        items={groupLabels}
                    />
                );
            },
        },
        // Agent assignment is not yet implemented
        // {
        //     id: 'agentName',
        //     header: i18n.t('th_has_agent.text'),
        //     accessorKey: 'agentName',
        //     cell: (cell: { getValue: () => string | null }) => (
        //         cell.getValue() ? <span key={cell.getValue()}>{cell.getValue()}</span> :
        //         <Icon name="mdi-close" className="text-danger" />
        //     ),
        // },
        {
            id: 'isSuperuser',
            header: i18n.t('th_superuser.text'),
            accessorKey: 'isSuperuser',
            cell: (cell: {getValue: () => boolean}) => (
                cell.getValue() === true ? <Icon name="mdi-check" className="text-success" /> :
                    <Icon name="mdi-close" className="text-danger" />
            ),
        },
        {
            id: 'active',
            header: i18n.t('th_active.text'),
            accessorKey: 'active',
            cell: (cell: {getValue: () => boolean}) => (
                cell.getValue() === true ? <Icon name="mdi-check" className="text-success" /> :
                    <Icon name="mdi-close" className="text-danger" />
            ),
        },
        {
            id: 'actions',
            header: i18n.t('th_action.text'),
            cell: (cell: {row: {original: User}}) => (
                <div className="d-flex align-items-center justify-content-end w-100">
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                id={`settings-dropdown-${cell.row.original.id}`}
                                data-cy="token-settings-toggle"
                            />

                            <Dropdown.Menu align="end">
                                {
                                    canChangeUser ? (
                                        <Dropdown.Item onClick={() => openSettingsModal(cell.row.original.id)}
                                            data-cy="user-settings-open-button"
                                            className="d-flex align-items-center gap-3">
                                            <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled
                                                    data-cy="user-settings-open-button"
                                                    className="d-flex align-items-center gap-3">
                                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {
                                    canViewUserActivity ? (
                                        <Dropdown.Item onClick={() => openActivityModal(cell.row.original.id)}
                                            data-cy="user-activity-open-button"
                                            className="d-flex align-items-center gap-3">
                                            <Icon name="mdi-timeline-outline" className="mr-2" /> {i18n.t('jt_activity.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled
                                                    data-cy="user-activity-open-button"
                                                    className="d-flex align-items-center gap-3">
                                                    <Icon name="mdi-timeline-outline" className="mr-2" /> {i18n.t('jt_activity.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {
                                    canViewUserLogins ? (
                                        <Dropdown.Item onClick={() => openLoginsModal(cell.row.original.id)}
                                            data-cy="user-logins-open-button"
                                            className="d-flex align-items-center gap-3">
                                            <Icon name="mdi-account-outline" className="mr-2" /> {i18n.t('jt_logins.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled
                                                    data-cy="user-logins-open-button"
                                                    className="d-flex align-items-center gap-3">
                                                    <Icon name="mdi-account-outline" className="mr-2" /> {i18n.t('jt_logins.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {
                                    canViewUserApiLogins ? (
                                        <Dropdown.Item onClick={() => openApiLoginsModal(cell.row.original.id)}
                                            data-cy="user-api-logins-open-button"
                                            className="d-flex align-items-center gap-3">
                                            <Icon name="mdi-api" className="mr-2" /> {i18n.t('jt_api_logins.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled
                                                    data-cy="user-api-logins-open-button"
                                                    className="d-flex align-items-center gap-3">
                                                    <Icon name="mdi-api" className="mr-2" /> {i18n.t('jt_api_logins.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {
                                    canViewUserAudit ? (
                                        <Dropdown.Item
                                            onClick={() =>
                                                openAuditModal(
                                                    cell.row.original.id,
                                                    `${cell.row.original.username} (${cell.row.original.email})`,
                                                )
                                            }
                                            data-cy="user-audit-open-button"
                                            className="d-flex align-items-center gap-3">
                                            <Icon name="mdi-chart-line" className="mr-2" /> {i18n.t('jt_user_audit.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled
                                                    data-cy="user-audit-open-button"
                                                    className="d-flex align-items-center gap-3">
                                                    <Icon name="mdi-chart-line" className="mr-2" /> {i18n.t('jt_user_audit.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {/* Agent assignment is not yet implemented */}
                                {/* {
                                    canAssignAgent ? (
                                        <Dropdown.Item onClick={() => openAssignAgentModal(cell.row.original.id, cell.row.original.username)}
                                            data-cy="assign-agent-open-button"
                                            className="d-flex align-items-center gap-3">
                                            <Icon name="mdi-account-plus-outline" className="mr-2" /> {i18n.t('jt_assign_agent.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled
                                                    data-cy="assign-agent-open-button"
                                                    className="d-flex align-items-center gap-3">
                                                    <Icon name="mdi-account-plus-outline" className="mr-2" /> {i18n.t('jt_assign_agent.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                } */}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <ConditionalNav>

            </ConditionalNav>

            <HelpTextBlock helpText={i18n.t('ht_the_users.text')} />

            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    {
                        canCreateUser ? (
                            <Tooltip content={i18n.t('jt_add.text')} placement="top">
                                <Button
                                    variant="info"
                                    size="sm"
                                    icon="mdi-plus"
                                    onClick={() => setSettingsModalOpen(true)}
                                    data-cy="new-user-btn"
                                    className='mb-2'
                                >
                                    {i18n.t('jt_add_new_user.text')}
                                </Button>
                            </Tooltip>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'inline-block'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon="mdi-plus"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="user-list-plus-btn"
                                        className='mb-2'
                                    >
                                        {i18n.t('jt_add_new_user.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </Col>
            </Row>

            <LoadingWrapper
                loading={loadingUsers}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="400px"
            >
                <div className="card">
                    <div className="card-body">
                        <TableComponent data={usersCleaned} columns={columns} />
                        <PaginationComponent
                            pages={pages}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            hideWhenSinglePage={false}
                        />
                    </div>
                </div>
                {
                    settingsModalOpen ? (
                        <UserSettingsModal
                            modalOpen={settingsModalOpen}
                            closeModal={closeSettingsModal}
                            activeUserId={selectedUserId}
                        />
                    ) : null
                }

                {
                    activityModalOpen ? (
                        <UserActivityModal
                            modalOpen={activityModalOpen}
                            closeModal={closeActivityModal}
                            activeUserId={selectedUserId}
                        />
                    ) : null
                }

                {
                    loginsModalOpen ? (
                        <UserLoginsModal
                            modalOpen={loginsModalOpen}
                            closeModal={closeLoginsModal}
                            activeUserId={selectedUserId}
                        />
                    ) : null
                }

                {
                    apiLoginsModalOpen ? (
                        <UserApiLoginsModal
                            modalOpen={apiLoginsModalOpen}
                            closeModal={closeApiLoginsModal}
                            activeUserId={selectedUserId}
                        />
                    ) : null
                }

                {
                    auditModalOpen ? (
                        <UserAuditModal
                            modalOpen={auditModalOpen}
                            closeModal={closeAuditModal}
                            activeUserId={selectedUserId}
                            auditedUserLabel={auditSubjectLabel}
                        />
                    ) : null
                }

                {/* {
                    assignAgentModalOpen ? (
                        <UserAssignAgentModal
                            modalOpen={assignAgentModalOpen}
                            closeModal={closeAssignAgentModal}
                            activeUserId={selectedUserId}
                            activeUserName={selectedUserName}
                        />
                    ) : null
                } */}
            </LoadingWrapper>
        </>
    );
});

export default UserListView; 